import {
  createAppSelector,
  pageGroupSelectors,
  pageGroupUserSelectors,
  pageSelectors,
  selectCurrentUserId,
  userSelectors,
} from "./base";
import { selectCurrentSectionUserId, selectEnabledSectionUsers } from "./section-user";
import { sortByFirstName } from "./sorting";
import { type ExtendedPageGroupUserType, type NestedPageGroupType } from "./types";

import type { PageGroupUserType } from "components/server-types";
import { filterMap } from "utils/collections";
import { isGroupPage } from "utils/page-utils";

export const selectAllPageGroupUsersByPageId = createAppSelector(
  [pageGroupUserSelectors.selectAll, pageGroupSelectors.filterByPageId],
  (pageGroupUsers, pageGroups) => {
    const pageGroupIds = new Set(pageGroups.map((pg) => pg.id));
    return pageGroupUsers.filter((pgu) => pageGroupIds.has(pgu.page_group_id));
  },
);

export const selectPageGroupUserForCurrentUserByPageId = createAppSelector(
  [selectAllPageGroupUsersByPageId, selectCurrentSectionUserId],
  (pageGroupUsers, sectionUserId) => {
    return pageGroupUsers.find((pgu) => pgu.section_user_id === sectionUserId) ?? null;
  },
);

export const selectPageGroupIdForCurrentUserByPageId = createAppSelector(
  [selectPageGroupUserForCurrentUserByPageId],
  (pageGroupUser) => {
    return pageGroupUser?.page_group_id ?? null;
  },
);

/** True when the current section user is ready (assigned) in a page group for the page. */
export const selectIsCurrentSectionUserAssignedToPageGroupByPageId = createAppSelector(
  [selectPageGroupUserForCurrentUserByPageId],
  (pageGroupUser) => !!pageGroupUser?.is_ready,
);

/** Page group id for the current user when they are ready (assigned); otherwise null. */
export const selectReadyPageGroupIdForCurrentUserByPageId = createAppSelector(
  [selectPageGroupUserForCurrentUserByPageId],
  (pageGroupUser) => (pageGroupUser?.is_ready ? pageGroupUser.page_group_id : null),
);

/**
 * Index of a page group among page groups for the page (ordered by created_at).
 * Returns -1 when pageGroupId is missing or not on the page.
 */
export const selectPageGroupIndexByPageGroupId = createAppSelector(
  [
    pageGroupSelectors.filterByPageId,
    (_state, _pageId: string | null | undefined, pageGroupId?: string | null) => pageGroupId,
  ],
  (pageGroups, pageGroupId) => {
    if (!pageGroupId) {
      return -1;
    }
    return pageGroups.findIndex((pageGroup) => pageGroup.id === pageGroupId);
  },
);

export const selectPageGroupUsersByPageId = createAppSelector(
  [
    pageSelectors.selectById,
    pageGroupUserSelectors.selectAll,
    pageGroupSelectors.filterByPageId,
    selectEnabledSectionUsers,
    userSelectors.selectEntities,
  ],
  (page, pageGroupUsers, pageGroups, enabledSectionUsers, userMap): ExtendedPageGroupUserType[] => {
    if (!page || !pageGroups || !isGroupPage(page)) {
      return [];
    }

    return pageGroupUsers.flatMap((pgu) => {
      const group = pageGroups.find((pg) => pg.id === pgu.page_group_id);
      if (!group) {
        return [];
      }

      const enabledSectionUser = enabledSectionUsers.find((cu) => cu.id === pgu.section_user_id);
      if (!enabledSectionUser) {
        return [];
      }

      const user = userMap[enabledSectionUser.user_id];
      if (!user) {
        return [];
      }

      return [
        {
          ...pgu,
          user_id: user.id,
          first_name: user.first_name,
          last_name: user.last_name,
          email: user.email,
        },
      ];
    });
  },
);

/** Page group users for a group (enabled section users only). */
export const selectPageGroupUsersByPageGroupId = createAppSelector(
  [
    (state, pageGroupId?: string | null) => pageGroupId,
    pageGroupUserSelectors.selectAll,
    selectEnabledSectionUsers,
  ],
  (pageGroupId, pageGroupUsers, enabledSectionUsers): PageGroupUserType[] => {
    if (!pageGroupId) {
      return [];
    }

    const enabledSectionUserIds = new Set(enabledSectionUsers.map((sectionUser) => sectionUser.id));

    return pageGroupUsers.filter(
      (pageGroupUser) =>
        pageGroupUser.page_group_id === pageGroupId &&
        enabledSectionUserIds.has(pageGroupUser.section_user_id),
    );
  },
);

/** Same as selectPageGroupUsersByPageGroupId, sorted by first name. */
export const selectSortedPageGroupUsersByPageGroupId = createAppSelector(
  [selectPageGroupUsersByPageGroupId, selectEnabledSectionUsers, userSelectors.selectEntities],
  (pageGroupUsers, enabledSectionUsers, userMap): PageGroupUserType[] =>
    pageGroupUsers.toSorted((pageGroupUserA, pageGroupUserB) => {
      const sectionUserA = enabledSectionUsers.find(
        (sectionUser) => sectionUser.id === pageGroupUserA.section_user_id,
      );
      const sectionUserB = enabledSectionUsers.find(
        (sectionUser) => sectionUser.id === pageGroupUserB.section_user_id,
      );
      const userA = sectionUserA ? userMap[sectionUserA.user_id] : null;
      const userB = sectionUserB ? userMap[sectionUserB.user_id] : null;
      if (!userA && !userB) return 0;
      if (!userA) return 1;
      if (!userB) return -1;
      return sortByFirstName(userA, userB);
    }),
);

export const selectPageGroupsWithUsersByPageId = createAppSelector(
  [pageGroupSelectors.filterByPageId, selectPageGroupUsersByPageId],
  (pageGroups, pageGroupUsers) =>
    pageGroups.map((page_group): NestedPageGroupType => {
      const pageGroupUsersForPageGroup = pageGroupUsers.filter(
        (pgu) => pgu.page_group_id === page_group.id,
      );
      return {
        ...page_group,
        page_group_users: pageGroupUsersForPageGroup,
      };
    }),
);

/** Count of page groups that have at least one (enabled) page_group_user. */
export const selectNonEmptyPageGroupCountByPageId = createAppSelector(
  [selectPageGroupsWithUsersByPageId],
  (pageGroups) => pageGroups.filter((pageGroup) => pageGroup.page_group_users.length > 0).length,
);

/** Count of page groups with no enabled page_group_users. */
export const selectEmptyPageGroupCountByPageId = createAppSelector(
  [pageGroupSelectors.filterByPageId, selectPageGroupUsersByPageId],
  (pageGroups, pageGroupUsers) => {
    const nonEmptyPageGroupIds = new Set(
      pageGroupUsers.map((pageGroupUser) => pageGroupUser.page_group_id),
    );
    return pageGroups.filter((pageGroup) => !nonEmptyPageGroupIds.has(pageGroup.id)).length;
  },
);

export const selectPageGroupUserForUser = createAppSelector(
  [
    (state, pageId?: string | null) => pageId,
    (state, pageId?: string | null, userId?: string | null) => userId,
    selectAllPageGroupUsersByPageId,
    selectEnabledSectionUsers,
  ],
  (pageId, userId, pageGroupUsers, sectionUsers) => {
    if (!pageId || !userId) {
      return null;
    }

    const sectionUserIdSet = new Set(
      filterMap(
        sectionUsers,
        (sectionUser) => sectionUser.user_id === userId,
        (sectionUser) => sectionUser.id,
      ),
    );

    const userPageGroupUser = pageGroupUsers.find((pgu) =>
      sectionUserIdSet.has(pgu.section_user_id),
    );

    return userPageGroupUser ?? null;
  },
);

export const selectNestedPageGroupForUser = createAppSelector(
  [selectPageGroupsWithUsersByPageId, selectPageGroupUserForUser],
  (nestedPageGroups, pageGroupUser): NestedPageGroupType | null => {
    if (!pageGroupUser) return null;
    return nestedPageGroups.find((pg) => pg.id === pageGroupUser.page_group_id) ?? null;
  },
);

/** True when confirm is unavailable: no group, or ready members already at max_group_size. */
export const selectIsConfirmGroupDisabledForUser = createAppSelector(
  [selectNestedPageGroupForUser, pageSelectors.selectById],
  (pageGroup, page) => {
    if (!pageGroup || !page) return true;
    if (page.max_group_size === null) return false;
    return (
      pageGroup.page_group_users.filter((pageGroupUser) => pageGroupUser.is_ready).length >=
      page.max_group_size
    );
  },
);

export const selectPageGroupForReadyUser = createAppSelector(
  [
    (state, pageId: string | null) => pageId,
    selectCurrentUserId,
    pageGroupSelectors.filterByPageId,
    pageGroupUserSelectors.selectAll,
    selectEnabledSectionUsers,
  ],
  (pageId, userId, page_groups, page_group_users, section_users) => {
    if (!pageId || !userId) {
      return null;
    }

    const sectionUserIdSet = new Set(
      filterMap(
        section_users,
        (section_user) => section_user.user_id === userId,
        (section_user) => section_user.id,
      ),
    );

    // Get a list of the user's page group ids
    const userGroupIdSet = new Set(
      filterMap(
        page_group_users,
        (group_user) => sectionUserIdSet.has(group_user.section_user_id) && group_user.is_ready,
        (group_user) => group_user.page_group_id,
      ),
    );

    // Go through the groups for the given page and find the group for the given user
    return page_groups.find((page_group) => userGroupIdSet.has(page_group.id));
  },
);

export const selectReadiedUsersInPageGroup = createAppSelector(
  [
    (state, pageGroupId: string | null) => pageGroupId,
    pageGroupUserSelectors.selectAll,
    selectEnabledSectionUsers,
    (state) => state.users,
  ],
  (pageGroupId, page_group_users, section_users, users) => {
    if (!pageGroupId) {
      return [];
    }

    // Get a list of the section_user's for this group
    const sectionUserIdSet = new Set(
      filterMap(
        page_group_users,
        (group_user) => group_user.page_group_id === pageGroupId && group_user.is_ready,
        (group_user) => group_user.section_user_id,
      ),
    );

    // Get a list of all users that match a section_user.user_id
    return filterMap(
      section_users,
      (section_user) => sectionUserIdSet.has(section_user.id),
      (section_user) => {
        const user = users[section_user.user_id];
        return {
          ...user,
        };
      },
    );
  },
);

export const selectReleasedPageGroupsForCurrentSection = createAppSelector(
  [pageGroupSelectors.selectAll, pageSelectors.filterForCurrentSection],
  (pageGroups, pages) => {
    const releasedPages = pages.filter((page) => page.released_at);
    const pageIds = new Set(releasedPages.map((page) => page.id));
    return pageGroups.filter((pageGroup) => pageIds.has(pageGroup.page_id));
  },
);

export const selectPageGroupUsersForCurrentSection = createAppSelector(
  [pageGroupUserSelectors.selectAll, selectReleasedPageGroupsForCurrentSection],
  (pageGroupUsers, pageGroups) => {
    const pageGroupIds = new Set(pageGroups.map((pageGroup) => pageGroup.id));
    return pageGroupUsers.filter((pageGroupUser) => pageGroupIds.has(pageGroupUser.page_group_id));
  },
);
